import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CreateWorkflowRequest } from '../models/Events/create-workflow-request';
import { WorkflowTemplate } from '../models/Events/workflow-template';
import { WorkflowActionTemplate } from '../models/Events/workflow-action-template';
import { CreateWorkflowActionRequest } from '../models/Events/create-workflow-action-request';
import { UpdateWorkflowActionRequest } from '../models/Events/update-workflow-action-request';

export class WorkflowApiError extends Error {
  constructor(
    public readonly errorCode: string,
    message: string,
    public readonly errorReference: string = ''
  ) {
    super(errorReference ? `${message} (Reference: ${errorReference})` : message);
    this.name = 'WorkflowApiError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class WorkflowService {

  constructor(private httpClient: HttpClient) {}

  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    observe: 'response' as const,
    responseType: 'json' as const,
    withCredentials: true
  };

  private body(response: any): any {
    const body = response?.body ?? response ?? {};
    if (body?.IsError) {
      throw new WorkflowApiError(
        body.ErrorCode || 'WORKFLOW_ERROR',
        body.ErrorMessage || 'The workflow request could not be completed.',
        body.ErrorReference || ''
      );
    }
    return body;
  }

  getWorkflows(): Observable<WorkflowTemplate[]> {
    return this.httpClient.get<WorkflowTemplate[]>(`${environment.apiUrl}workflows`, this.httpOptions).pipe(
      map((res: any) => {
        const body = this.body(res);
        const list = body?.Workflows ?? body ?? [];
        return (list as any[]).map(item => new WorkflowTemplate(item));
      })
    );
  }

  createWorkflow(request: CreateWorkflowRequest): Observable<WorkflowTemplate> {
    const payload = new CreateWorkflowRequest(request);
    return this.httpClient.post<WorkflowTemplate>(`${environment.apiUrl}workflows`, payload, this.httpOptions).pipe(
      map((res: any) => {
        const body = this.body(res);
        const created = body?.Workflow ?? body ?? {};
        return new WorkflowTemplate({
          WorkflowID: (created as any).WorkflowID ?? -1,
          Name: (created as any).Name ?? payload.Name,
          Description: (created as any).Description ?? payload.Description,
          ActionCount: (created as any).ActionCount ?? 0,
          LastUpdated: (created as any).LastUpdated ?? new Date()
        });
      })
    );
  }

  getWorkflowActions(workflowId: number): Observable<WorkflowActionTemplate[]> {
    return this.httpClient.get<WorkflowActionTemplate[]>(`${environment.apiUrl}workflows/${workflowId}/actions`, this.httpOptions).pipe(
      map((res: any) => {
        const body = this.body(res);
        const list = body?.Actions ?? body ?? [];
        return (list as any[]).map(item => new WorkflowActionTemplate(item));
      })
    );
  }

  createWorkflowAction(workflowId: number, request: CreateWorkflowActionRequest): Observable<WorkflowActionTemplate> {
    const payload = new CreateWorkflowActionRequest({ ...request, WorkflowID: workflowId });
    return this.httpClient.post<WorkflowActionTemplate>(`${environment.apiUrl}workflows/${workflowId}/actions`, payload, this.httpOptions).pipe(
      map((res: any) => {
        const body = this.body(res);
        const action = body?.Action ?? body ?? payload;
        return new WorkflowActionTemplate({
          WorkflowID: workflowId,
          WorkflowActionID: (action as any).WorkflowActionID ?? payload.WorkflowID,
          Title: (action as any).Title ?? payload.Title,
          Description: (action as any).Description ?? payload.Description,
          DefaultOwnerUserID: (action as any).DefaultOwnerUserID ?? payload.DefaultOwnerUserID,
          DefaultOwnerTypeID: (action as any).DefaultOwnerTypeID ?? payload.DefaultOwnerTypeID,
          DefaultDueInDays: (action as any).DefaultDueInDays ?? payload.DefaultDueInDays,
          IsMandatory: (action as any).IsMandatory ?? payload.IsMandatory,
          ActionVariationID: (action as any).ActionVariationID ?? payload.ActionVariationID ?? 1,
          AuditID: (action as any).AuditID ?? payload.AuditID ?? null,
          Seq: (action as any).Seq ?? 0
        });
      })
    );
  }

  // legacy update/delete removed in favor of post routes above

  updateWorkflow(workflowId: number, request: CreateWorkflowRequest): Observable<WorkflowTemplate> {
    const payload = { WorkflowID: workflowId, Name: request.Name, Description: request.Description };
    return this.httpClient.post<WorkflowTemplate>(`${environment.apiUrl}workflows/update`, payload, this.httpOptions).pipe(
      map((res: any) => {
        const body = this.body(res);
        const wf = body?.Workflow ?? body ?? payload;
        return new WorkflowTemplate({
          ...wf,
          WorkflowID: workflowId
        });
      })
    );
  }

  deleteWorkflow(workflowId: number): Observable<boolean> {
    const payload = { WorkflowID: workflowId };
    return this.httpClient.post(`${environment.apiUrl}workflows/delete`, payload, this.httpOptions).pipe(
      map((res: any) => {
        this.body(res);
        return true;
      })
    );
  }

  updateWorkflowAction(request: UpdateWorkflowActionRequest): Observable<WorkflowActionTemplate> {
    const payload = new UpdateWorkflowActionRequest(request);
    return this.httpClient.post<WorkflowActionTemplate>(`${environment.apiUrl}workflows/actions/update`, payload, this.httpOptions).pipe(
      map((res: any) => {
        const body = this.body(res);
        const action = body?.Action ?? body ?? payload;
        return new WorkflowActionTemplate({
          ...action,
          WorkflowActionID: payload.WorkflowActionID,
          WorkflowID: payload.WorkflowID,
          ActionVariationID: (action as any).ActionVariationID ?? payload.ActionVariationID ?? 1,
          AuditID: (action as any).AuditID ?? payload.AuditID ?? null
        });
      })
    );
  }

  deleteWorkflowAction(request: { WorkflowID: number; WorkflowActionID: number }): Observable<boolean> {
    return this.httpClient.post(`${environment.apiUrl}workflows/actions/delete`, request, this.httpOptions).pipe(
      map((res: any) => {
        this.body(res);
        return true;
      })
    );
  }

  getWorkflowLocations(workflowId: number): Observable<number[]> {
    return this.httpClient.get(`${environment.apiUrl}workflows/${workflowId}/locations`, this.httpOptions).pipe(
      map((res: any) => {
        const body = this.body(res);
        const list = body?.LocationIDs ?? body ?? [];
        return (list as any[]).map(x => Number(x));
      })
    );
  }

  saveWorkflowLocations(workflowId: number, locationIds: number[]): Observable<boolean> {
    const payload = { WorkflowID: workflowId, LocationIDs: locationIds || [] };
    return this.httpClient.post(`${environment.apiUrl}workflows/${workflowId}/locations`, payload, this.httpOptions).pipe(
      map((res: any) => {
        this.body(res);
        return true;
      })
    );
  }
}
